import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/services/who-do-i-call')({
  component: WhoDoICallPage,
  head: () => ({
    meta: [
      { title: 'Who do I call? State government agency directory — Warren-Blackwood' },
      {
        name: 'description',
        content:
          'Plain-English directory: which WA state agency handles education, health, housing, roads, police, environment, welfare, water and electricity — with phone numbers and websites.',
      },
      { property: 'og:title', content: 'Who do I call? — Warren-Blackwood Electorate Guide' },
      {
        property: 'og:description',
        content:
          'Find the right WA state agency for schools, hospitals, public housing, road faults, water billing, power outages and more.',
      },
    ],
  }),
});

type AgencyContact = {
  agency: string;
  what: string;
  phone?: { label: string; tel: string; note?: string }[];
  website?: { label: string; href: string };
  tips?: string[];
};

type Topic = {
  id: string;
  title: string;
  blurb: string;
  contacts: AgencyContact[];
};

const TOPICS: Topic[] = [
  {
    id: 'education',
    title: 'Education — schools, TAFE',
    blurb:
      'Public schools (primary and secondary) are run by the WA Department of Education. Start with the school itself; escalate to the regional office if it isn\'t resolved.',
    contacts: [
      {
        agency: 'Department of Education WA',
        what: 'Complaints, enrolment, bullying, curriculum, staff conduct in public schools.',
        phone: [{ label: 'General enquiries', tel: '0892644111', note: '(08) 9264 4111' }],
        website: { label: 'education.wa.edu.au', href: 'https://www.education.wa.edu.au/' },
        tips: [
          'Talk to the classroom teacher first, then the principal, then the regional office.',
          'Warren-Blackwood schools sit under the South West Education Region.',
        ],
      },
      {
        agency: 'South Regional TAFE',
        what: 'Trade and vocational training in Manjimup, Margaret River, Albany, Bunbury.',
        phone: [{ label: 'Student services', tel: '1800621445', note: '1800 621 445' }],
        website: { label: 'southregionaltafe.wa.edu.au', href: 'https://www.southregionaltafe.wa.edu.au/' },
      },
      {
        agency: 'Catholic Education WA / Independent schools',
        what: 'For non-government schools, contact the school directly; system-level issues go to the relevant authority.',
        website: { label: 'cewa.edu.au', href: 'https://www.cewa.edu.au/' },
      },
    ],
  },
  {
    id: 'health',
    title: 'Health — hospitals, mental health',
    blurb:
      'Public hospitals and community health in this electorate are run by the WA Country Health Service. Medicare and the PBS are federal.',
    contacts: [
      {
        agency: 'Emergency',
        what: 'Life-threatening — ambulance, police, fire.',
        phone: [{ label: 'Triple Zero', tel: '000', note: '000' }],
      },
      {
        agency: 'WA Country Health Service — South West / Great Southern',
        what: 'Hospitals at Margaret River, Augusta, Bridgetown, Manjimup, Pemberton, Denmark; community nursing; patient transport.',
        phone: [{ label: 'Bunbury (SW regional office)', tel: '0897221000', note: '(08) 9722 1000' }],
        website: { label: 'wacountry.health.wa.gov.au', href: 'https://www.wacountry.health.wa.gov.au/' },
      },
      {
        agency: 'HealthDirect — 24/7 nurse line',
        what: 'Free health advice when you\'re not sure if you need a GP or ED.',
        phone: [{ label: '24/7', tel: '1800022222', note: '1800 022 222' }],
        website: { label: 'healthdirect.gov.au', href: 'https://www.healthdirect.gov.au/' },
      },
      {
        agency: 'Mental Health Emergency Response Line (Rurallink)',
        what: 'After-hours mental health crisis support for country WA.',
        phone: [{ label: '24/7', tel: '1800552002', note: '1800 552 002' }],
      },
      {
        agency: 'Health & Disability Services Complaints Office (HaDSCO)',
        what: 'Independent complaints about WA hospitals, GPs, allied health.',
        phone: [{ label: 'Office', tel: '1800813583', note: '1800 813 583' }],
        website: { label: 'hadsco.wa.gov.au', href: 'https://www.hadsco.wa.gov.au/' },
      },
    ],
  },
  {
    id: 'housing',
    title: 'Housing — public housing, bond, tenancy',
    blurb:
      'Public and social housing in WA is run by the Department of Communities (Housing). Private rental disputes go to Consumer Protection.',
    contacts: [
      {
        agency: 'Department of Communities — Housing',
        what: 'Public housing applications, transfers, rent, repairs to a Housing property.',
        phone: [{ label: 'General', tel: '1800093325', note: '1800 093 325' }],
        website: { label: 'wa.gov.au/communities', href: 'https://www.wa.gov.au/organisation/department-of-communities' },
        tips: ['Manjimup has the nearest Communities office for the southern part of the electorate.'],
      },
      {
        agency: 'Consumer Protection — Tenancy',
        what: 'Bond, rent increases, repairs in a private rental, ending a lease.',
        phone: [{ label: 'Tenancy line', tel: '1300304054', note: '1300 304 054' }],
        website: { label: 'commerce.wa.gov.au/consumer-protection', href: 'https://www.commerce.wa.gov.au/consumer-protection' },
      },
      {
        agency: 'Homelessness — Entrypoint Perth',
        what: 'Statewide assessment and referral if you are homeless or at risk.',
        phone: [{ label: '7 days', tel: '1800124684', note: '1800 124 684' }],
        website: { label: 'entrypointperth.com.au', href: 'https://www.entrypointperth.com.au/' },
      },
    ],
  },
  {
    id: 'roads',
    title: 'Roads — state highways, council roads',
    blurb:
      'Main Roads WA manages state highways (South Western Hwy, South Coast Hwy, Vasse Hwy). Your local shire manages every other road, plus footpaths and verges.',
    contacts: [
      {
        agency: 'Main Roads WA',
        what: 'Potholes, signs, lights, crash debris on state highways.',
        phone: [{ label: 'Customer Care (24/7)', tel: '138138', note: '138 138' }],
        website: { label: 'mainroads.wa.gov.au', href: 'https://www.mainroads.wa.gov.au/' },
      },
      {
        agency: 'Your local shire',
        what: 'Council roads, footpaths, verges, street trees, drainage.',
        website: { label: 'See shire pages', href: '/' },
      },
      {
        agency: 'Heavy Vehicle Services (Main Roads WA)',
        what: 'Oversize / overmass permits, restricted access vehicle network queries.',
        phone: [{ label: 'HVS', tel: '138486', note: '138 486' }],
      },
    ],
  },
  {
    id: 'police',
    title: 'Police — crime, traffic, safety',
    blurb:
      'WA Police is a state agency. Triple Zero for emergencies; 131 444 for non-urgent. Each town has a local station.',
    contacts: [
      {
        agency: 'Emergency',
        what: 'Crime in progress, immediate danger.',
        phone: [{ label: 'Triple Zero', tel: '000', note: '000' }],
      },
      {
        agency: 'WA Police — non-urgent',
        what: 'Report past incidents, suspicious behaviour, lost property.',
        phone: [{ label: 'Police Assistance', tel: '131444', note: '131 444' }],
        website: { label: 'police.wa.gov.au', href: 'https://www.police.wa.gov.au/' },
      },
      {
        agency: 'Crime Stoppers',
        what: 'Anonymous tip-offs.',
        phone: [{ label: '24/7', tel: '1800333000', note: '1800 333 000' }],
        website: { label: 'crimestopperswa.com.au', href: 'https://www.crimestopperswa.com.au/' },
      },
    ],
  },
  {
    id: 'environment',
    title: 'Environment — parks, pollution, wildlife',
    blurb:
      'Department of Biodiversity, Conservation and Attractions (DBCA / "Parks and Wildlife") manages national parks and state forest. DWER handles pollution and water licensing.',
    contacts: [
      {
        agency: 'DBCA — Parks and Wildlife (Warren / South Coast Region)',
        what: 'National parks, state forest, bushfires on Parks land, injured native wildlife.',
        phone: [
          { label: 'Donnelly District (Pemberton)', tel: '0897761207', note: '(08) 9776 1207' },
          { label: 'Wildcare Helpline (injured wildlife)', tel: '0894749055', note: '(08) 9474 9055' },
        ],
        website: { label: 'dbca.wa.gov.au', href: 'https://www.dbca.wa.gov.au/' },
      },
      {
        agency: 'Department of Water and Environmental Regulation (DWER)',
        what: 'Pollution reports, water licences, contaminated sites.',
        phone: [{ label: 'Pollution Watch (24/7)', tel: '1300784782', note: '1300 784 782' }],
        website: { label: 'wa.gov.au/dwer', href: 'https://www.wa.gov.au/organisation/department-of-water-and-environmental-regulation' },
      },
      {
        agency: 'DPIRD — Pest & Disease',
        what: 'Report new pests, weeds, or livestock disease.',
        phone: [{ label: 'Pest and Disease Information', tel: '1800084881', note: '1800 084 881' }],
        website: { label: 'agric.wa.gov.au', href: 'https://www.agric.wa.gov.au/' },
      },
    ],
  },
  {
    id: 'welfare',
    title: 'Welfare — Centrelink, child protection, family',
    blurb:
      'Income support is federal (Services Australia / Centrelink). Child protection and family support are state (Department of Communities).',
    contacts: [
      {
        agency: 'Services Australia — Centrelink',
        what: 'JobSeeker, Age Pension, Family Tax Benefit, Disability Support Pension.',
        phone: [
          { label: 'Employment services', tel: '132850', note: '132 850' },
          { label: 'Families', tel: '136150', note: '136 150' },
          { label: 'Older Australians', tel: '132300', note: '132 300' },
        ],
        website: { label: 'servicesaustralia.gov.au', href: 'https://www.servicesaustralia.gov.au/' },
      },
      {
        agency: 'Department of Communities — Child Protection',
        what: 'Concerns about a child\'s safety or wellbeing.',
        phone: [
          { label: 'Central Intake (business hours)', tel: '1800273889', note: '1800 273 889' },
          { label: 'Crisis Care (after hours)', tel: '1800199008', note: '1800 199 008' },
        ],
        website: { label: 'wa.gov.au/communities', href: 'https://www.wa.gov.au/organisation/department-of-communities' },
      },
      {
        agency: '1800RESPECT',
        what: 'National family, domestic and sexual violence counselling — 24/7.',
        phone: [{ label: '24/7', tel: '1800737732', note: '1800 737 732' }],
        website: { label: '1800respect.org.au', href: 'https://www.1800respect.org.au/' },
      },
    ],
  },
  {
    id: 'water',
    title: 'Water — billing, supply, leaks',
    blurb:
      'Water Corporation is the state-owned water utility for scheme (town) water. Rural properties on tank or bore water are not Water Corp customers.',
    contacts: [
      {
        agency: 'Water Corporation',
        what: 'Bills, meter reads, scheme water supply, sewerage connections.',
        phone: [
          { label: 'Accounts and general', tel: '131385', note: '13 13 85' },
          { label: 'Faults and emergencies (24/7)', tel: '131375', note: '13 13 75' },
        ],
        website: { label: 'watercorporation.com.au', href: 'https://www.watercorporation.com.au/' },
      },
      {
        agency: 'Energy and Water Ombudsman WA',
        what: 'Unresolved disputes with Water Corp, Synergy or Western Power.',
        phone: [{ label: 'Free call', tel: '1800754004', note: '1800 754 004' }],
        website: { label: 'ombudsman.wa.gov.au', href: 'https://www.ombudsman.wa.gov.au/' },
      },
      {
        agency: 'DWER — Water licensing',
        what: 'Bore licences, dam approvals, taking water from a river or aquifer.',
        website: { label: 'wa.gov.au/dwer', href: 'https://www.wa.gov.au/organisation/department-of-water-and-environmental-regulation' },
      },
    ],
  },
  {
    id: 'electricity',
    title: 'Electricity & gas — outages, billing',
    blurb:
      'In the south-west, Western Power runs the poles and wires and Synergy sells you the electricity. Two different numbers depending on the problem.',
    contacts: [
      {
        agency: 'Western Power',
        what: 'Power outages, fallen lines (do not touch), streetlights, new connections.',
        phone: [
          { label: 'Faults and emergencies (24/7)', tel: '131351', note: '13 13 51' },
          { label: 'General enquiries', tel: '131087', note: '13 10 87' },
        ],
        website: { label: 'westernpower.com.au', href: 'https://www.westernpower.com.au/' },
      },
      {
        agency: 'Synergy',
        what: 'Electricity bills, payment plans, hardship support, moving house.',
        phone: [{ label: 'Residential', tel: '131353', note: '13 13 53' }],
        website: { label: 'synergy.net.au', href: 'https://www.synergy.net.au/' },
      },
      {
        agency: 'ATCO Gas',
        what: 'Natural gas supply and faults (limited reticulated coverage in this electorate).',
        phone: [{ label: 'Gas leaks / emergency 24/7', tel: '131352', note: '13 13 52' }],
        website: { label: 'atco.com/au', href: 'https://www.atco.com/en-au.html' },
      },
      {
        agency: 'Energy and Water Ombudsman WA',
        what: 'Unresolved bills or supply complaints.',
        phone: [{ label: 'Free call', tel: '1800754004', note: '1800 754 004' }],
        website: { label: 'ombudsman.wa.gov.au', href: 'https://www.ombudsman.wa.gov.au/' },
      },
    ],
  },
];

function WhoDoICallPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.4rem',
    color: 'var(--rivergum)',
    marginTop: 0,
    marginBottom: 'var(--sp-xs)',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.6,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };

  const card: React.CSSProperties = {
    background: 'var(--warm-white, #faf7f0)',
    border: '1px solid var(--light-border)',
    borderRadius: 10,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  };

  const agencyBox: React.CSSProperties = {
    background: '#fff',
    border: '1px solid var(--light-border)',
    borderRadius: 8,
    padding: '0.75rem 0.9rem',
    marginTop: '0.6rem',
  };

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 820,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services · Who do I call?
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Who do I call?
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        A plain-English directory of WA state agencies. Pick a topic, get the
        right phone number and website. If you can&apos;t get a resolution, the{' '}
        <Link to="/contact" style={link}>state MP&apos;s office</Link> can help
        escalate.
      </p>

      <nav
        aria-label="Topics on this page"
        style={{
          background: '#eef5fb',
          border: '1px solid #b9d4e8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginBottom: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#1b4f72' }}>Jump to a topic</strong>
        <ul
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
            gap: '0.4rem 1rem',
            listStyle: 'none',
            padding: 0,
            margin: '0.6rem 0 0',
          }}
        >
          {TOPICS.map((t) => (
            <li key={t.id}>
              <a href={`#${t.id}`} style={link}>
                {t.title.split('—')[0].trim()}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {TOPICS.map((topic) => (
        <section key={topic.id} id={topic.id} style={card}>
          <h2 style={h2}>{topic.title}</h2>
          <p style={{ ...p, marginBottom: 0 }}>{topic.blurb}</p>

          {topic.contacts.map((c, i) => (
            <div key={i} style={agencyBox}>
              <div
                style={{
                  fontWeight: 700,
                  color: 'var(--ink)',
                  marginBottom: '0.15rem',
                }}
              >
                {c.agency}
              </div>
              <div
                style={{
                  color: 'var(--ink-mid)',
                  fontSize: '0.95rem',
                  marginBottom: '0.4rem',
                  lineHeight: 1.5,
                }}
              >
                {c.what}
              </div>
              {c.phone && c.phone.length > 0 && (
                <div style={{ fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                  {c.phone.map((ph, j) => (
                    <div key={j}>
                      <span style={{ color: 'var(--ink-mid)' }}>{ph.label}: </span>
                      <a href={`tel:${ph.tel}`} style={link}>
                        {ph.note ?? ph.tel}
                      </a>
                    </div>
                  ))}
                </div>
              )}
              {c.website && (
                <div style={{ fontSize: '0.95rem' }}>
                  <a href={c.website.href} style={link}>
                    {c.website.label} →
                  </a>
                </div>
              )}
              {c.tips && c.tips.length > 0 && (
                <ul
                  style={{
                    margin: '0.4rem 0 0',
                    paddingLeft: '1.1rem',
                    color: 'var(--ink-mid)',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                  }}
                >
                  {c.tips.map((tip, k) => (
                    <li key={k}>{tip}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      ))}

      <div
        style={{
          background: '#fdf6e3',
          border: '1px solid #e8d9a8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginTop: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#7a5b1e' }}>Still stuck?</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          If an agency isn&apos;t responding or you&apos;ve hit a dead end, contact your{' '}
          <Link to="/contact" style={link}>state MP&apos;s office</Link>. MPs can&apos;t
          override agency decisions, but they can chase a response and raise
          systemic problems in parliament.
        </p>
      </div>
    </main>
  );
}
